import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import suburbsData from "../../../data/us_addresses.json";
import SupportChildComponent from "./SupportChildComponent";
import WorkerSupportTypesComponent from "./WorkerSupportTypesComponent";
import WorkerCareActivitiesComponent from "./WorkerCareActivitiesComponent";
import WorkerSupportGenderComponent from "./WorkerSupportGenderComponent";
import SearchWorkersResultsComponent from "./SearchWorkersResultsComponent";
import WorkerProfileResultsComponent from "./WorkerProfileResultsComponent";
import { useSearchWorkers } from "../../../context/SearchWorkersContext";
import {
  getStepFromPath,
  handleUseCurrentLocation,
  handleSuburbChange,
  handleSuggestionClick,
  handleClearSuburb
} from "../../../util_functions/helpers";
import { FaCrosshairs, FaSpinner, FaSearch } from "react-icons/fa";

const SearchWorkers = () => {
  const { searchSelections, saveSearchSelections, updateSearchSelectionField } =
    useSearchWorkers();
  const [suburbValue, setSuburbValue] = useState("");
  const [childDetails, setChildDetails] = useState(null);
  const [supportTypeDetails, setSupportTypeDetails] = useState(null);
  const [careActivityDetails, setCareActivityDetails] = useState([]);
  const [genderDetails, setGenderDetails] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [addressValidationMessage, setAddressValidationMessage] = useState("");
  const [validationMessageClass, setValidationMessageClass] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showProfileView, setShowProfileView] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const stepSegment = location.pathname.match(/search-workers\/step-(\d+)/);
    if (stepSegment) {
      const stepNumber = parseInt(stepSegment[1], 10);
      setCurrentStep(stepNumber);
      setShowSteps(true);
    } else {
      setCurrentStep(1);
      setShowSteps(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (searchSelections) {
      console.log(
        "[SearchWorkers] Syncing state with searchSelections:",
        searchSelections
      );
      setSuburbValue(searchSelections.suburbValue || "");
      setChildDetails(searchSelections.childDetails || null);
      setSupportTypeDetails(searchSelections.supportTypeDetails || null);
      setCareActivityDetails(searchSelections.careActivityDetails || []);
      setGenderDetails(searchSelections.genderDetails || null);
    }
  }, [searchSelections]);

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    setCurrentStep(getStepFromPath());
  }, [window.location.pathname]);

  const updateURL = (step) => {
    navigate(`/account/fs-dashboard/search-workers/step-${step}`, {});
  };

  const steps = [
    SupportChildComponent,
    WorkerSupportTypesComponent,
    WorkerCareActivitiesComponent,
    WorkerSupportGenderComponent,
    SearchWorkersResultsComponent,
    WorkerProfileResultsComponent,
  ];

  const stepIndicators = steps
    .slice(0, 5)
    .map((_, index) => `Step ${index + 1}`);

  const handleSearchClick = (e) => {
    e.preventDefault();
    setAddressValidationMessage("");

    if (!suburbValue) {
      setAddressValidationMessage("Please enter a suburb.");
      setValidationMessageClass("address-invalid");
      inputRef.current.focus();
      return;
    }

    const isSuburbValid = suburbsData.Suburbs_Zipcodes.some((suburb) => {
      const formattedSuburb =
        `${suburb.Post_City} ${suburb.State} ${suburb.Zip_Code}`.toLowerCase();
      return formattedSuburb === suburbValue.toLowerCase().trim();
    });
    if (!isSuburbValid) {
      setAddressValidationMessage("Please select a valid location.");
      setValidationMessageClass("address-invalid");
      return;
    }

    saveSearchSelections(
      {
        suburbValue,
        childDetails,
        supportTypeDetails,
        careActivityDetails,
        genderDetails,
      },
      true
    );

    setShowSteps(true);
    setCurrentStep(1);
    updateURL(1);
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return childDetails !== null;
      case 2:
        return supportTypeDetails && supportTypeDetails.length > 0;
      case 3:
        return careActivityDetails && careActivityDetails.length > 0;
      case 4:
        return genderDetails !== null;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (validateStep()) {
      setIsError(false);
      switch (currentStep) {
        case 1:
          await updateSearchSelectionField("childDetails", childDetails);
          break;
        case 2:
          await updateSearchSelectionField(
            "supportTypeDetails",
            supportTypeDetails
          );
          break;
        case 3:
          await updateSearchSelectionField(
            "careActivityDetails",
            careActivityDetails
          );
          break;
        case 4:
          await updateSearchSelectionField("genderDetails", genderDetails);
          break;
        default:
          console.error("Invalid step for updating search selections.");
      }
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      updateURL(nextStep);
    } else {
      setIsError(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prevStep) => prevStep - 1);
    } else {
      setShowSteps(false);
      navigate("/account/fs-dashboard/search-workers");
    }
  };

  const clearError = useCallback(() => {
    setIsError(false);
  }, []);

  const handleViewProfile = (user) => {
    setSelectedUser(user);
    setShowProfileView(true);
    const profileStep = currentStep + 1;
    setCurrentStep(profileStep);
    updateURL(profileStep);
  };

  const handleCloseProfile = () => {
    setShowProfileView(false);
    const previousStep = currentStep - 1;
    setCurrentStep(previousStep);
    updateURL(previousStep);
  };

  const scrollableSteps = [2, 3, 4];

  const renderStepContent = (step) => {
    const containerClass = scrollableSteps.includes(step)
      ? "search-workers-container-scrollable"
      : "search-workers-container-no-scroll";

    return (
      <div className={containerClass}>
        {(() => {
          switch (step) {
            case 1:
              return (
                <SupportChildComponent
                  onUpdate={setChildDetails}
                  supportChildValue={childDetails}
                  isError={isError}
                  clearError={clearError}
                />
              );
            case 2:
              return (
                <WorkerSupportTypesComponent
                  onUpdate={setSupportTypeDetails}
                  supportDetailsValue={supportTypeDetails}
                  isError={isError}
                  clearError={clearError}
                />
              );
            case 3:
              return (
                <WorkerCareActivitiesComponent
                  onUpdate={setCareActivityDetails}
                  selectedSupportTypes={supportTypeDetails}
                  careActivityDetailsValue={careActivityDetails}
                  isError={isError}
                  clearError={clearError}
                />
              );
            case 4:
              return (
                <WorkerSupportGenderComponent
                  onUpdate={setGenderDetails}
                  supportGenderValue={genderDetails}
                  isError={isError}
                  clearError={clearError}
                />
              );
            case 5:
              return (
                <SearchWorkersResultsComponent
                  onViewProfile={handleViewProfile}
                  suburb={suburbValue}
                  childDetails={childDetails}
                  supportTypeDetails={supportTypeDetails}
                  careActivityDetails={careActivityDetails}
                  genderDetails={genderDetails}
                />
              );
            case 6:
              return (
                <WorkerProfileResultsComponent
                  workerUserId={selectedUser?.id}
                  onClose={handleCloseProfile}
                />
              );
            default:
              return null;
          }
        })()}
      </div>
    );
  };

  return (
    <div className="search-workers-container">
      {!showSteps ? (
        <div className="search-support-workers">
          <div className="validation-message-container">
            {addressValidationMessage && (
              <div className={validationMessageClass}>
                {addressValidationMessage}
              </div>
            )}
          </div>

          <button
            type="button"
            className="use-location-button"
            onClick={() =>
              handleUseCurrentLocation({
                setSuburbValue,
                setAddressValidationMessage,
                setValidationMessageClass,
                setIsLocating,
              })
            }
            disabled={isLocating}
          >
            {isLocating ? (
              <>
                <FaSpinner className="icon spin" />
                Locating…
              </>
            ) : (
              <>
                <FaCrosshairs className="icon" />
                Use current location
              </>
            )}
          </button>

          <form>
            <div className="search-input-container">

              <div className="search-input-button-wrapper">
                <div className="search-input-with-icon">
                  <FaSearch className="location-search-icon" />
                  <input
                    className="search-location-input with-icon"
                    ref={inputRef}
                    type="text"
                    value={suburbValue}
                    onChange={(e) => handleSuburbChange(e, setSuburbValue, setSuggestions)}
                    placeholder="Type City or ZIP Code then select..."
                  />
                  {suburbValue && (
                    <span
                      onClick={() =>
                        handleClearSuburb(
                          setSuburbValue,
                          setSuggestions,
                          setAddressValidationMessage,
                          inputRef
                        )
                      }
                      className="search-clear-icon"
                    >
                      X
                    </span>
                  )}
                </div>
              </div>

              <button
                className="search-location-button"
                onClick={handleSearchClick}
                disabled={!suburbValue.trim()}
              >
                Find
              </button>
            </div>


            <div className="search-location-suggestions">
              <ul>
                {suggestions.map((address, index) => (
                  <li
                    key={index}
                    onClick={() =>
                      handleSuggestionClick(
                        `${address.Post_City} ${address.State} ${address.Zip_Code}`,
                        setSuburbValue,
                        setSuggestions
                      )
                    }
                  >
                    {address.Post_City} {address.State} {address.Zip_Code}
                  </li>
                ))}
              </ul>
            </div>

          </form>
        </div>
      ) : (
        <div
          className={`step-content-container ${scrollableSteps.includes(currentStep)
            ? "search-workers-container-scrollable"
            : "search-workers-container-no-scroll"
            }`}
        >
          {showSteps && currentStep <= 5 && (
            <div className="step-indicators">
              {stepIndicators.map((indicator, index) => (
                <div key={index} className="step-wrapper">
                  <div
                    className={`step-circle ${currentStep === index + 1 ? "active" : ""
                      } ${currentStep > index + 1 ? "completed" : ""}`}
                    onClick={() => {
                      setCurrentStep(index + 1);
                      updateURL(index + 1);
                    }}
                  >
                    {index + 1}
                  </div>
                  {index < stepIndicators.length - 1 && (
                    <div
                      className={`step-line ${currentStep > index + 1 ? "completed" : ""
                        }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          )}
          {currentStep === 6 ? (
            <WorkerProfileResultsComponent
              workerUserId={selectedUser?.id}
              onClose={handleCloseProfile}
            />
          ) : (
            renderStepContent(currentStep)
          )}
          {currentStep < 5 && !showProfileView && (
            <div className="support-worker-steps-actions">
              {currentStep > 0 && currentStep < steps.length && (
                <button className="previous-button" onClick={handleBack}>
                  Previous
                </button>
              )}
              {currentStep < 4 ? (
                <button className="continue-button" onClick={handleNext}>
                  Continue
                </button>
              ) : currentStep === 4 ? (
                <button className="continue-button" onClick={handleNext}>
                  See results
                </button>
              ) : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchWorkers;
